import React from "react";
import {
  Link,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
} from "@mui/material";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { LeadStageSummaryRow } from "../../../models/Dashboard/LeadAnalytics/LeadAnalytics";
import { formatDateWithoutTime } from "../common/functions/formatDate";

interface LeadStageSummaryTableProps {
  rows: LeadStageSummaryRow[];
  page_no: number;
  limit: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
}

const KYLAS_LEAD_DETAILS_URL = "https://app.kylas.io/sales/leads/details/";

const sortableColumns = [
  { key: "lead_id", label: "Lead ID" },
  { key: "owner_name", label: "Owner Name" },
  { key: "mql", label: "MQL (hrs)" },
  { key: "non_contactable", label: "Non Contactable (hrs)" },
  { key: "open", label: "Open (hrs)" },
  { key: "total_time", label: "Total Time (hrs)" },
  { key: "final_stage_name", label: "Final Stage" },
  { key: "company_name", label: "Company Name" },
  { key: "created_date", label: "Created Date" },
  { key: "modified_date", label: "Modified Date" },
];

const LeadStageSummaryTable: React.FC<LeadStageSummaryTableProps> = ({
  rows,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const formatDateValue = (value: string | null) => {
    if (!value) return "-";
    const formatted = formatDateWithoutTime(value);
    return formatted || "-";
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 180,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Typography variant="body2">No lead analytics found.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {sortableColumns.map((column) => (
                <AppTableHeaderCell
                  key={column.key}
                  sx={{
                    textAlign: "center",
                    py: 1,
                    px: 1,
                    whiteSpace: "nowrap",
                    "& .MuiTableSortLabel-root": { fontSize: "0.76rem", fontWeight: 600 },
                  }}
                >
                  <TableSortLabel
                    active={sortBy === column.key}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        column.key,
                        sortBy === column.key && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon(column.key)}</span>}
                  >
                    {column.label}
                  </TableSortLabel>
                </AppTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.lead_id} hover sx={{ "& td": { py: 0.9 } }}>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  <Link
                    href={`${KYLAS_LEAD_DETAILS_URL}${row.lead_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontWeight: 600,
                      color: "#6455ED",
                      fontSize: "0.78rem",
                    }}
                  >
                    {row.lead_id}
                  </Link>
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.owner_name || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.mql}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.non_contactable}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.open}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.total_time}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.final_stage_name || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {row.company_name || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {formatDateValue(row.created_date)}
                </AppTableRowCell>
                <AppTableRowCell sx={{ textAlign: "center", fontSize: "0.78rem", px: 1 }}>
                  {formatDateValue(row.modified_date)}
                </AppTableRowCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default LeadStageSummaryTable;
